import type {
  TrainingAnswer,
  TrainingAnswers,
  TrainingModuleSchema,
  TrainingOption,
  TrainingPageDef,
  TrainingPlayerPage,
  TrainingQuestionDef,
} from './trainingModule';
import { trainingQuestions, schemaHasQuestionGroup, isFixedPageType } from './trainingModule';

/**
 * The player projection and the grader, shared between the server and the
 * builder preview so the two can never drift. Both are parameterised over
 * the only things that legitimately differ per caller:
 *
 *   - how a matching label's display id is derived (the server hashes a
 *     per-task seed so the id neither equals nor reveals the pair id; the
 *     preview uses a plain prefix because the admin authored the answers
 *     and nothing is secret there), and
 *   - how display order is shuffled (seeded PRNG on the server so a resume
 *     sees the same order; a stable rotation in the preview).
 *
 * Whatever derivation is used, the projection and the grader must be given
 * the same one: the grader recomputes matching label ids rather than
 * storing them.
 */

/** Derives the display id for a matching label from its pair id. */
export type TrainingMatchLabelIdFn = (pairId: string) => string;

/**
 * Normalise a short-text answer for comparison: trim, lower-case, and collapse
 * internal whitespace runs to a single space. Shared by the grader so the
 * builder preview and the server agree on what "matches".
 */
export function normaliseShortText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

export interface TrainingProjectionHooks {
  matchLabelId: TrainingMatchLabelIdFn;
  /** Reorder ordering items so the authored (correct) order never shows. */
  shuffleOrdering: (items: TrainingOption[], pageId: string) => TrainingOption[];
  /** Reorder the matching right column. */
  shuffleLabels: (labels: TrainingOption[], pageId: string) => TrainingOption[];
}

/* -------------------------------------------------------------------------
 * Question banks + randomisation
 *
 * Two version-level settings let a module draw a random subset of its
 * questions, in a random order, fresh per attempt:
 *   serveQuestionCount — null serves every question; N serves N of them.
 *   shuffleQuestions   — present the served questions in a shuffled order.
 *
 * The randomness is an injected seeded shuffle so the projection and the
 * grader compute the SAME served set: on the server it is mulberry32 seeded
 * with the task id plus a per-attempt nonce (stable on resume, redrawn next
 * attempt); in the builder preview it is a stable rotation. Both the player
 * projection and the grader must be handed the same options.
 * -----------------------------------------------------------------------*/

export interface TrainingServeOptions {
  /** Null serves every question; otherwise the number of questions to serve. */
  serveQuestionCount: number | null;
  /** Whether the served questions are presented in a shuffled order. */
  shuffleQuestions: boolean;
  /**
   * Seeded shuffle of the question list, deterministic per attempt. Selection
   * takes the first serveQuestionCount of this; presentation order uses it
   * directly when shuffleQuestions is on.
   */
  shuffle: <T extends { id: string }>(items: T[]) => T[];
}

/** How many questions a serve yields, clamped to [1, available]. */
function servedCount(available: number, serveQuestionCount: number | null): number {
  if (serveQuestionCount == null) return available;
  return Math.max(1, Math.min(serveQuestionCount, available));
}

/**
 * The questions a serve yields, in served order. Pure given the injected
 * shuffle, so the projection and the grader agree on exactly which questions
 * count (and, when shuffled, in what order). A serve-all-no-shuffle config
 * is a no-op and returns the questions untouched.
 */
export function selectServedQuestions<T extends { id: string }>(
  questions: T[],
  serve: TrainingServeOptions,
): T[] {
  const n = servedCount(questions.length, serve.serveQuestionCount);
  if (n >= questions.length && !serve.shuffleQuestions) return questions.slice();
  const chosen = serve.shuffle(questions).slice(0, n);
  if (serve.shuffleQuestions) return chosen;
  // Selection kept, but presented in authored order.
  const ids = new Set(chosen.map((q) => q.id));
  return questions.filter((q) => ids.has(q.id));
}

/**
 * Apply question banks + randomisation to a page list (player projection or
 * authoring schema). Slides keep their position; the question slots are
 * filled in page order by the served subset, and any leftover question slots
 * are dropped. A no-op serve returns the pages unchanged.
 *
 * Question groups are fixed pages like slides: one slot cannot stand in for
 * the several questions a grid holds. Callers reject the combination at
 * publish time (validateQuestionGroupSettings) and skip the serve entirely
 * for such a schema; this is the belt-and-braces half of that rule.
 */
export function applyQuestionServe<T extends { id: string; type: string }>(
  pages: T[],
  serve: TrainingServeOptions,
): T[] {
  const slots: number[] = [];
  const questions: T[] = [];
  pages.forEach((p, i) => {
    if (!isFixedPageType(p.type)) {
      slots.push(i);
      questions.push(p);
    }
  });
  if (questions.length === 0) return pages.slice();
  const chosen = selectServedQuestions(questions, serve);
  const fill = new Map<number, T>();
  chosen.forEach((q, k) => fill.set(slots[k]!, q));
  // Slots beyond the chosen count are surplus and drop out entirely.
  const dropped = new Set(slots.slice(chosen.length));
  const out: T[] = [];
  pages.forEach((p, i) => {
    if (fill.has(i)) out.push(fill.get(i)!);
    else if (!dropped.has(i)) out.push(p);
  });
  return out;
}

/**
 * Project an authoring schema to the answer-free player pages. This is the
 * leak boundary for quiz answers: the returned pages structurally carry no
 * correct_* keys and no hotspot regions, and the ordering items / matching
 * right column are reordered via the hooks so the authored position (which
 * IS the answer for those types) never reaches the client as-is.
 */
export function projectTrainingPlayerPages(
  schema: TrainingModuleSchema,
  hooks: TrainingProjectionHooks,
  serve?: TrainingServeOptions,
): TrainingPlayerPage[] {
  const effective = effectiveServe(schema, serve);
  const pages = effective ? applyQuestionServe(schema.pages, effective) : schema.pages;
  return pages.map((page): TrainingPlayerPage => {
    const projected = projectOnePage(page, hooks);
    // Slides pass through whole (section_id kept); questions are rebuilt, so
    // re-attach the section so the player can group them.
    return page.type !== 'slide' && page.section_id
      ? { ...projected, section_id: page.section_id }
      : projected;
  });
}

/**
 * The serve config that actually applies. A schema with question groups
 * cannot be subset or shuffled (see applyQuestionServe), so the config is
 * dropped — computed from the schema alone so the projection and the grader
 * always reach the same answer.
 */
function effectiveServe(
  schema: TrainingModuleSchema,
  serve: TrainingServeOptions | undefined,
): TrainingServeOptions | undefined {
  if (!serve) return undefined;
  return schemaHasQuestionGroup(schema) ? undefined : serve;
}

/** Project a single page; the shuffles/label ids come from the hooks. */
function projectOnePage(page: TrainingPageDef, hooks: TrainingProjectionHooks): TrainingPlayerPage {
  switch (page.type) {
    case 'slide':
      return page;
    case 'question_group':
      // Layout passes through; only each row's correct column is stripped.
      return {
        id: page.id,
        type: 'question_group',
        ...(page.title !== undefined ? { title: page.title } : {}),
        ...(page.intro !== undefined ? { intro: page.intro } : {}),
        options: page.options.map((o) => ({ id: o.id, label: o.label })),
        rows: page.rows.map((r) => ({
          id: r.id,
          prompt: r.prompt,
          points: r.points,
          ...(r.optional ? { optional: true } : {}),
        })),
        ...(page.image_key !== undefined ? { image_key: page.image_key } : {}),
        ...(page.image_position !== undefined ? { image_position: page.image_position } : {}),
        ...(page.image_align !== undefined ? { image_align: page.image_align } : {}),
        ...(page.image_size !== undefined ? { image_size: page.image_size } : {}),
        ...(page.image_caption !== undefined ? { image_caption: page.image_caption } : {}),
        ...(page.image_alt !== undefined ? { image_alt: page.image_alt } : {}),
      };
    case 'text_group':
    case 'follow_up':
      // Nothing secret on either: no answer key exists for text answers, and
      // a follow-up's questions are derived from the learner's own answers.
      return { ...page };
    case 'single_choice':
    case 'multi_select':
      return {
        ...questionBase(page),
        type: page.type,
        options: page.options.map((o) => ({ id: o.id, label: o.label })),
      };
    case 'true_false':
      return { ...questionBase(page), type: 'true_false' };
    case 'short_text':
      // `accepted` is the answer; it must never reach the player.
      return {
        ...questionBase(page),
        type: 'short_text',
        ...(page.multiline ? { multiline: true } : {}),
        ...(page.placeholder !== undefined ? { placeholder: page.placeholder } : {}),
        ...(page.input !== undefined ? { input: page.input } : {}),
      };
    case 'categorize':
      // category_id on each item is the answer; strip it and shuffle the
      // items so their authored grouping never hints at the buckets.
      return {
        ...questionBase(page),
        type: 'categorize',
        categories: page.categories.map((c) => ({ id: c.id, label: c.label })),
        items: hooks.shuffleLabels(
          page.items.map((it) => ({ id: it.id, label: it.label })),
          page.id,
        ),
      };
    case 'ordering':
      return {
        ...questionBase(page),
        type: 'ordering',
        items: hooks.shuffleOrdering(
          page.items.map((o) => ({ id: o.id, label: o.label })),
          page.id,
        ),
      };
    case 'hotspot':
      return { ...questionBase(page), type: 'hotspot', image_key: page.image_key };
    case 'matching':
      return {
        ...questionBase(page),
        type: 'matching',
        targets: page.pairs.map((p) => ({ id: p.id, label: p.left })),
        labels: hooks.shuffleLabels(
          page.pairs.map((p) => ({ id: hooks.matchLabelId(p.id), label: p.right })),
          page.id,
        ),
      };
  }
}

/** The answer-free keys every projected question carries. */
function questionBase(q: TrainingQuestionDef): {
  id: string;
  prompt: string;
  points: number;
  image_key?: string;
  optional?: boolean;
} {
  return {
    id: q.id,
    prompt: q.prompt,
    points: q.points,
    ...(q.image_key ? { image_key: q.image_key } : {}),
    ...(q.optional ? { optional: true } : {}),
  };
}

/* -------------------------------------------------------------------------
 * Grading. Exact-match, full points per question (v1): a question is
 * either fully correct or scores zero.
 * -----------------------------------------------------------------------*/

export interface TrainingQuestionGrade {
  question_id: string;
  correct: boolean;
  points_earned: number;
}

/** Per-section result, when the schema defines sections. */
export interface TrainingSectionGrade {
  section_id: string;
  title: string;
  earned: number;
  total: number;
  /** Percentage 0..100, 2dp. */
  score: number;
  /** Whether the section's pass mark was met. Ungated or unserved => true. */
  passed: boolean;
  /** True when the attempt was served no questions from this section. */
  not_applicable: boolean;
}

export interface TrainingAttemptGrade {
  earned: number;
  total: number;
  /** Percentage 0..100, 2dp. */
  score: number;
  per_question: TrainingQuestionGrade[];
  /** Per-section breakdown; empty when the schema defines no sections. */
  sections: TrainingSectionGrade[];
}

/**
 * Grade one question. Unanswered or malformed answers score zero, and so
 * does a question with no authored correct answer: those only exist in an
 * ungraded module, which never reaches the grader for a verdict.
 */
export function gradeTrainingQuestion(
  q: TrainingQuestionDef,
  answer: TrainingAnswer | undefined,
  matchLabelId: TrainingMatchLabelIdFn,
): boolean {
  switch (q.type) {
    case 'single_choice':
      return (
        q.correct_option_id !== undefined &&
        typeof answer === 'string' &&
        answer === q.correct_option_id
      );
    case 'multi_select': {
      if (!Array.isArray(answer) || q.correct_option_ids === undefined) return false;
      const chosen = new Set(answer);
      // Compare as sets: the validator rejects duplicate correct ids, but a
      // version published before that check existed must still grade sanely.
      const correct = new Set(q.correct_option_ids);
      if (chosen.size !== correct.size) return false;
      return [...correct].every((id) => chosen.has(id));
    }
    case 'true_false':
      return q.correct !== undefined && typeof answer === 'boolean' && answer === q.correct;
    case 'short_text': {
      if (typeof answer !== 'string' || q.accepted === undefined) return false;
      const given = normaliseShortText(answer);
      return given !== '' && q.accepted.some((a) => normaliseShortText(a) === given);
    }
    case 'categorize': {
      if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return false;
      const placed = answer as Record<string, string>;
      // Every item must sit in its authored category.
      return q.items.every((it) => placed[it.id] === it.category_id);
    }
    case 'ordering': {
      if (!Array.isArray(answer) || answer.length !== q.items.length) return false;
      return q.items.every((item, i) => answer[i] === item.id);
    }
    case 'hotspot': {
      if (
        typeof answer !== 'object' ||
        answer === null ||
        Array.isArray(answer) ||
        typeof (answer as { x?: unknown }).x !== 'number' ||
        typeof (answer as { y?: unknown }).y !== 'number'
      ) {
        return false;
      }
      const { x, y } = answer as { x: number; y: number };
      return q.regions.some((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);
    }
    case 'matching': {
      if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return false;
      const placed = answer as Record<string, string>;
      return q.pairs.every((p) => placed[p.id] === matchLabelId(p.id));
    }
  }
}

/**
 * Grade a whole attempt against an immutable version's schema. When a serve
 * is supplied only the served questions are graded and the total is their
 * points, so a subset attempt is scored out of what it was actually asked.
 */
export function gradeTrainingAttempt(
  schema: TrainingModuleSchema,
  answers: TrainingAnswers,
  matchLabelId: TrainingMatchLabelIdFn,
  serve?: TrainingServeOptions,
): TrainingAttemptGrade {
  const all = trainingQuestions(schema);
  // Groups drop the serve config, exactly as the projection does, so the
  // grader always scores the questions the learner was actually shown.
  const effective = serve && !schemaHasQuestionGroup(schema) ? serve : undefined;
  const questions = effective ? selectServedQuestions(all, effective) : all;
  let earned = 0;
  let total = 0;
  const per_question: TrainingQuestionGrade[] = questions.map((q) => {
    total += q.points;
    const correct = gradeTrainingQuestion(q, answers[q.id], matchLabelId);
    if (correct) earned += q.points;
    return { question_id: q.id, correct, points_earned: correct ? q.points : 0 };
  });
  // total >= 1 is guaranteed by publish-time validation (>= 1 question,
  // points >= 1), but guard the division anyway for unvalidated fixtures.
  const score = total > 0 ? Math.round((earned / total) * 10000) / 100 : 0;
  const sections = gradeSections(schema, questions, per_question);
  return { earned, total, score, per_question, sections };
}

/**
 * Per-section results over the served questions. A section grades only the
 * questions actually served into it; one the bank draw skipped entirely
 * (total 0) is not-applicable rather than a fail, since the learner was never
 * asked it. Ungated sections always pass.
 */
function gradeSections(
  schema: TrainingModuleSchema,
  served: TrainingQuestionDef[],
  perQuestion: TrainingQuestionGrade[],
): TrainingSectionGrade[] {
  const defs = schema.sections ?? [];
  if (defs.length === 0) return [];
  const earnedById = new Map(perQuestion.map((p) => [p.question_id, p.points_earned]));
  return defs.map((def) => {
    let earned = 0;
    let total = 0;
    for (const q of served) {
      if (q.section_id !== def.id) continue;
      total += q.points;
      earned += earnedById.get(q.id) ?? 0;
    }
    const not_applicable = total === 0;
    const score = total > 0 ? Math.round((earned / total) * 10000) / 100 : 0;
    const passed =
      def.pass_mark == null || not_applicable ? true : isTrainingPass(earned, total, def.pass_mark);
    return { section_id: def.id, title: def.title, earned, total, score, passed, not_applicable };
  });
}

/**
 * Pass test in integers so 79.999% float artefacts can't sneak past a
 * pass mark: earned/total >= pass_mark/100 without the division.
 */
export function isTrainingPass(earned: number, total: number, pass_mark: number): boolean {
  return earned * 100 >= pass_mark * total;
}

/**
 * Whether a graded attempt passes: the overall pass mark must be met AND
 * every gated section cleared. The single rule both the server and the
 * builder preview use for full_retake scoring.
 */
export function trainingAttemptPassed(grade: TrainingAttemptGrade, passMark: number): boolean {
  if (!isTrainingPass(grade.earned, grade.total, passMark)) return false;
  return grade.sections.every((s) => s.passed);
}
